import { type ReactElement } from 'react';
import { mapChipKeyToStatusTone } from '@/features/shared/utils/tableMetricPresentation';
import { isLightTheme } from '@/theme/semanticTokens';
import { s, typographyTokens } from '@/theme/tokens';
import { formatDuration, formatFrequency, formatPercent } from '@/ui';
import { getThresholdChipKey } from '@/ui/composites/table';
import { Box, Text, type Theme } from '@/ui/primitives';
import {
  resolveTableMetricAccentColor,
  resolveTableMetricAccentTone,
} from './tableMetricAccentStyles';
import type { MetricThreshold } from '@/shared/utils/metricsThresholds';
import type { SemanticColorKey } from '@/theme/semanticTokens';
import type { StatusTone } from '@/ui/primitives/Chip';

export type TableV2MetricChipKind = 'avgDuration' | 'flakeScore' | 'frequency' | 'passRate';

type TableV2MetricChipProps = {
  kind: TableV2MetricChipKind;
  threshold: MetricThreshold;
  value: number | null | undefined;
};

type TableV2MetricValueProps = {
  accentWidth?: string;
  label: string;
  tone: ReturnType<typeof mapChipKeyToStatusTone> | StatusTone;
  valueColor?: SemanticColorKey;
  variant?: 'detail' | 'table';
};

const tableV2MetricValueGap = s('md');

const resolveDefaultTableMetricAccentWidth = (theme: Theme): string =>
  isLightTheme(theme) ? '3px' : '2px';

const formatTableV2MetricChipValue = (
  kind: TableV2MetricChipKind,
  value: number | null | undefined,
): string => {
  if (value === null || value === undefined) return '-';
  if (kind === 'avgDuration') return formatDuration(value);
  if (kind === 'frequency') return formatFrequency(value);
  return `${formatPercent(value)}%`;
};

const getTableV2MetricThresholdValue = (
  kind: TableV2MetricChipKind,
  value: number | null | undefined,
): number | null | undefined => {
  if (kind === 'avgDuration' && value !== null && value !== undefined) return value / 1000;
  return value;
};

export const TableV2MetricValue = ({
  accentWidth,
  label,
  tone,
  valueColor,
  variant = 'table',
}: TableV2MetricValueProps): ReactElement => {
  if (label === '-') {
    return (
      <Text
        role="body"
        component="span"
        color={variant === 'detail' ? 'textPrimary' : 'textSecondary'}
      >
        -
      </Text>
    );
  }

  const metricTone = resolveTableMetricAccentTone(tone);

  return (
    <Box
      component="span"
      sx={{
        alignItems: 'center',
        display: 'inline-flex',
        height: variant === 'detail' ? 'auto' : '22px',
        justifyContent: 'flex-start',
        maxWidth: '100%',
        minWidth: 0,
        pl: tableV2MetricValueGap,
        position: 'relative',
        '&::before': {
          backgroundColor: (theme) => resolveTableMetricAccentColor(theme, metricTone),
          content: '""',
          height: variant === 'detail' ? '18px' : '100%',
          left: 0,
          position: 'absolute',
          top: '50%',
          transform: 'translateY(-50%)',
          width:
            accentWidth ?? (variant === 'table' ? resolveDefaultTableMetricAccentWidth : '2px'),
        },
      }}
    >
      <Text
        role="body"
        component="span"
        color={valueColor ?? (variant === 'detail' ? 'textPrimary' : undefined)}
        sx={[
          {
            color:
              valueColor === undefined && variant === 'table'
                ? (theme) =>
                    isLightTheme(theme) ? 'rgba(0, 0, 0, 0.72)' : 'rgba(244, 247, 251, 0.74)'
                : undefined,
            fontVariantNumeric: 'tabular-nums',
            fontWeight: typographyTokens.bodyMedium.fontWeight,
            lineHeight: 1.25,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
          variant === 'table' ? { fontSize: typographyTokens.captionSmall.fontSize } : {},
        ]}
      >
        {label}
      </Text>
    </Box>
  );
};

export const TableV2MetricChip = ({
  kind,
  threshold,
  value,
}: TableV2MetricChipProps): ReactElement => {
  if (value === null || value === undefined) {
    return (
      <Text role="body" component="span" color="textSecondary">
        -
      </Text>
    );
  }

  const thresholdValue = getTableV2MetricThresholdValue(kind, value);
  const tone = mapChipKeyToStatusTone(getThresholdChipKey(thresholdValue, threshold));

  return <TableV2MetricValue label={formatTableV2MetricChipValue(kind, value)} tone={tone} />;
};
